// Pure MusicXML -> intermediate representation (IR) parser.
//
// Scope (v1): extract the structural information needed to drive a FlowFrame
// click/drone/timeline — measure count, time signature per measure, initial
// tempo + tempo markings, key signature, rehearsal letters. Does NOT extract
// notes/pitches/chord symbols yet (deferred until reference-tone v2).
//
// Input:  a MusicXML XML string (uncompressed; .mxl zip is rejected upstream).
// Output: an IR object — see parseMusicXML JSDoc below.
//
// The IR is intentionally decoupled from FlowFrame's pattern shape so this
// parser stays trivially testable and so a future caller (e.g. an OMR pipeline)
// can reuse it without dragging in App.js state.

/**
 * @typedef {Object} MusicXMLMeasureIR
 * @property {number} number              Measure number as printed in the XML (may be 0 for a pickup).
 * @property {number} numerator           Time signature top number, carried forward from prior measures.
 * @property {number} denominator         Time signature bottom number, carried forward from prior measures.
 * @property {string|null} rehearsalLetter First rehearsal mark in this measure, if any.
 */

/**
 * @typedef {Object} MusicXMLTempoIR
 * @property {number} measureIndex  0-based index into IR.measures where the marking appears.
 * @property {number} tempo         BPM (referenced to a quarter note per MusicXML convention).
 */

/**
 * @typedef {Object} MusicXMLIR
 * @property {string|null} title
 * @property {number} initialTempo                  BPM. Defaults to 120 if no <sound tempo> marking exists.
 * @property {MusicXMLTempoIR[]} tempoMarkings      All tempo markings in order, including the initial one.
 * @property {MusicXMLMeasureIR[]} measures
 * @property {number|null} keyFifths                -7..+7 (negative = flats, positive = sharps). First key encountered.
 * @property {('major'|'minor'|null)} keyMode
 */

const DEFAULT_TEMPO = 120;

function parseXMLString(xmlString) {
  if (typeof xmlString !== "string" || xmlString.trim() === "") {
    throw new Error("MusicXML input is empty.");
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlString, "application/xml");

  // DOMParser reports parse errors as a <parsererror> element inside the doc.
  const parseError = doc.getElementsByTagName("parsererror")[0];
  if (parseError) {
    throw new Error(
      "Could not parse file as XML. " +
        (parseError.textContent || "").trim().split("\n")[0]
    );
  }
  return doc;
}

function textOf(parent, tagName) {
  if (!parent) return null;
  const el = parent.getElementsByTagName(tagName)[0];
  if (!el) return null;
  const text = el.textContent;
  return text == null ? null : text.trim();
}

function parseIntOrNull(s) {
  if (s == null) return null;
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : null;
}

function parseFloatOrNull(s) {
  if (s == null) return null;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

// Find direct children matching a tag name. (querySelectorAll without
// descendant matches across nested <part> boundaries.)
function directChildren(parent, tagName) {
  const out = [];
  if (!parent) return out;
  for (let i = 0; i < parent.childNodes.length; i++) {
    const node = parent.childNodes[i];
    if (node.nodeType === 1 && node.nodeName === tagName) {
      out.push(node);
    }
  }
  return out;
}

function extractTitle(doc) {
  const root = doc.documentElement;
  // <work><work-title> or <movement-title>
  const workEl = root.getElementsByTagName("work")[0];
  const workTitle = textOf(workEl, "work-title");
  if (workTitle) return workTitle;
  const movementTitle = textOf(root, "movement-title");
  if (movementTitle) return movementTitle;
  return null;
}

function extractTimeSignatureFromMeasure(measureEl, currentNumerator, currentDenominator) {
  // <attributes><time><beats>X</beats><beat-type>Y</beat-type></time></attributes>
  const attributesList = directChildren(measureEl, "attributes");
  for (const attrs of attributesList) {
    const timeEls = directChildren(attrs, "time");
    for (const timeEl of timeEls) {
      const beats = parseIntOrNull(textOf(timeEl, "beats"));
      const beatType = parseIntOrNull(textOf(timeEl, "beat-type"));
      if (beats && beats > 0) currentNumerator = beats;
      if (beatType && beatType > 0) currentDenominator = beatType;
    }
  }
  return { numerator: currentNumerator, denominator: currentDenominator };
}

function extractKeyFromMeasure(measureEl) {
  // <attributes><key><fifths>0</fifths><mode>major</mode></key></attributes>
  const attributesList = directChildren(measureEl, "attributes");
  for (const attrs of attributesList) {
    const keyEls = directChildren(attrs, "key");
    for (const keyEl of keyEls) {
      const fifths = parseIntOrNull(textOf(keyEl, "fifths"));
      const modeText = textOf(keyEl, "mode");
      const mode =
        modeText && modeText.toLowerCase() === "minor"
          ? "minor"
          : modeText && modeText.toLowerCase() === "major"
          ? "major"
          : null;
      if (fifths != null || mode) {
        return { fifths, mode };
      }
    }
  }
  return null;
}

function extractTempoMarkingsFromMeasure(measureEl) {
  // <sound tempo="N"/> can be a direct child of <measure> or nested in <direction>.
  // We want every occurrence in document order.
  const out = [];
  const soundEls = measureEl.getElementsByTagName("sound");
  for (let i = 0; i < soundEls.length; i++) {
    const tempoAttr = soundEls[i].getAttribute("tempo");
    if (tempoAttr) {
      const tempo = parseFloatOrNull(tempoAttr);
      if (tempo && tempo > 0) out.push(tempo);
    }
  }
  return out;
}

function extractRehearsalLetterFromMeasure(measureEl) {
  // <direction><direction-type><rehearsal>A</rehearsal></direction-type></direction>
  const rehearsalEls = measureEl.getElementsByTagName("rehearsal");
  if (rehearsalEls.length === 0) return null;
  const text = (rehearsalEls[0].textContent || "").trim();
  return text || null;
}

/**
 * Parse a MusicXML string into the FlowFrame intermediate representation.
 *
 * @param {string} xmlString
 * @returns {MusicXMLIR}
 * @throws {Error} if the input is not parseable, not score-partwise, or has no measures.
 */
export function parseMusicXML(xmlString) {
  const doc = parseXMLString(xmlString);
  const root = doc.documentElement;

  if (!root) {
    throw new Error("MusicXML file has no root element.");
  }
  if (root.nodeName === "score-timewise") {
    throw new Error(
      "score-timewise format is not supported. Re-export the score in score-partwise format."
    );
  }
  if (root.nodeName !== "score-partwise") {
    throw new Error(
      `Not a MusicXML score (expected <score-partwise>, got <${root.nodeName}>).`
    );
  }

  const title = extractTitle(doc);

  const parts = directChildren(root, "part");
  if (parts.length === 0) {
    throw new Error("MusicXML score contains no <part> elements.");
  }
  // Use the first part for measure structure. In a well-formed score, time
  // signature, tempo, and rehearsal marks agree across parts at any given
  // measure, so the first part is sufficient for the timeline.
  const firstPart = parts[0];
  const measureEls = directChildren(firstPart, "measure");
  if (measureEls.length === 0) {
    throw new Error("MusicXML part contains no <measure> elements.");
  }

  let currentNumerator = 4;
  let currentDenominator = 4;
  let keyFifths = null;
  let keyMode = null;
  /** @type {MusicXMLMeasureIR[]} */
  const measures = [];
  /** @type {MusicXMLTempoIR[]} */
  const tempoMarkings = [];

  measureEls.forEach((measureEl, idx) => {
    const numberAttr = measureEl.getAttribute("number");
    const numberParsed = parseIntOrNull(numberAttr);
    const measureNumber = numberParsed != null ? numberParsed : idx + 1;

    const ts = extractTimeSignatureFromMeasure(
      measureEl,
      currentNumerator,
      currentDenominator
    );
    currentNumerator = ts.numerator;
    currentDenominator = ts.denominator;

    if (keyFifths === null && keyMode === null) {
      const key = extractKeyFromMeasure(measureEl);
      if (key) {
        keyFifths = key.fifths;
        keyMode = key.mode;
      }
    }

    const tempos = extractTempoMarkingsFromMeasure(measureEl);
    for (const tempo of tempos) {
      tempoMarkings.push({ measureIndex: idx, tempo });
    }

    measures.push({
      number: measureNumber,
      numerator: currentNumerator,
      denominator: currentDenominator,
      rehearsalLetter: extractRehearsalLetterFromMeasure(measureEl),
    });
  });

  const initialTempo = tempoMarkings.length > 0 ? tempoMarkings[0].tempo : DEFAULT_TEMPO;

  return {
    title,
    initialTempo,
    tempoMarkings,
    measures,
    keyFifths,
    keyMode,
  };
}

export const __testing = {
  DEFAULT_TEMPO,
};
